import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Camera, ChevronLeft, ChevronRight, Image as ImageIcon, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import Footer from "@/components/landing/Footer";
import { LanguageProvider, useLang } from "@/i18n/LanguageContext";
import { PHOTO_ALBUMS } from "@/data/photoAlbums";

const WORDS = {
  es: {
    back: "Volver a KNDLA",
    eyebrow: "KNDLA / RECUERDOS",
    title: "Fotografías",
    intro: "Cada fiesta tiene su historia. Revive nuestros mejores momentos.",
    albums: "Álbumes",
    photos: "fotos",
    emptyTitle: "Próximamente, nuevos álbumes",
    emptyBody: "Estamos preparando las fotografías de nuestras fiestas. Vuelve pronto para verlas.",
    explore: "Explorar fiestas",
    notFound: "No encontramos este álbum",
    notFoundBody: "Puede que el enlace haya cambiado o el álbum ya no esté disponible.",
    all: "Todos los álbumes",
    prev: "Fotografía anterior",
    next: "Fotografía siguiente",
    close: "Cerrar fotografía",
  },
  en: {
    back: "Back to KNDLA",
    eyebrow: "KNDLA / MEMORIES",
    title: "Photos",
    intro: "Every party has a story. Relive our best moments.",
    albums: "Albums",
    photos: "photos",
    emptyTitle: "Photo albums coming soon",
    emptyBody: "We're preparing photos from our parties. Check back soon.",
    explore: "Explore parties",
    notFound: "Album not found",
    notFoundBody: "This album may have been moved or is no longer available.",
    all: "All albums",
    prev: "Previous photo",
    next: "Next photo",
    close: "Close photo",
  },
  ru: {
    back: "Вернуться на KNDLA",
    eyebrow: "KNDLA / ВОСПОМИНАНИЯ",
    title: "Фотографии",
    intro: "У каждой вечеринки своя история. Вспомни лучшие моменты вместе с нами.",
    albums: "Альбомы",
    photos: "фото",
    emptyTitle: "Фотоальбомы скоро появятся",
    emptyBody: "Мы готовим фотографии с наших вечеринок. Заглядывай позже.",
    explore: "Посмотреть вечеринки",
    notFound: "Альбом не найден",
    notFoundBody: "Возможно, ссылка изменилась или альбом больше недоступен.",
    all: "Все альбомы",
    prev: "Предыдущее фото",
    next: "Следующее фото",
    close: "Закрыть фото",
  },
};

function dateLabel(date, lang) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || "")) return date || "";
  const [year, month, day] = date.split("-").map(Number);
  const locales = { es: "es-ES", en: "en-GB", ru: "ru-RU" };
  return new Intl.DateTimeFormat(locales[lang] || "en-GB", {
    year: "numeric", month: "long", day: "numeric", timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

function PhotosBody() {
  const { albumSlug } = useParams();
  const { locale } = useLang();
  const lang = String(locale || "en").toLowerCase().split("-")[0];
  const w = WORDS[lang] || WORDS.en;
  const albums = [...PHOTO_ALBUMS].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const album = albumSlug ? albums.find((entry) => entry.slug === albumSlug) : null;
  const images = album?.photos || [];
  const [activeImage, setActiveImage] = useState(null);

  useEffect(() => {
    setActiveImage(null);
  }, [albumSlug]);

  useEffect(() => {
    if (activeImage === null) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setActiveImage(null);
      if (event.key === "ArrowRight") setActiveImage((i) => (i + 1) % images.length);
      if (event.key === "ArrowLeft") setActiveImage((i) => (i + images.length - 1) % images.length);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeImage, images.length]);

  return (
    <div className="min-h-screen bg-coal font-manrope text-white">
      <div className="grain-overlay" aria-hidden="true" />
      <header className="relative z-10 border-b border-white/10 bg-coal/95">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-5 py-5 md:px-8">
          <Link to="/" aria-label="KNDLA — Home" className="inline-flex items-center">
            <Logo />
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-white/70 transition-colors hover:border-flame hover:text-white">
            <ArrowLeft className="h-4 w-4" /> {w.back}
          </Link>
        </div>
      </header>

      <main className="relative z-10 mx-auto min-h-[60vh] max-w-7xl px-5 py-14 md:px-8 md:py-20">
        <p className="text-xs font-bold uppercase tracking-[0.35em] text-flame">{w.eyebrow}</p>
        {albumSlug ? (
          album ? (
            <>
              <Link to="/photos" className="mt-6 inline-flex items-center gap-2 text-sm text-white/60 hover:text-flame">
                <ArrowLeft className="h-4 w-4" /> {w.all}
              </Link>
              <h1 className="mt-6 font-syne text-4xl font-extrabold tracking-tighter sm:text-6xl">{album.title}</h1>
              <p className="mt-3 text-sm text-white/50">{dateLabel(album.date, lang)} · {images.length} {w.photos}</p>
              {images.length ? (
                <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5">
                  {images.map((src, index) => (
                    <button
                      key={`${src}-${index}`}
                      type="button"
                      onClick={() => setActiveImage(index)}
                      aria-label={`${album.title} — ${index + 1}`}
                      className="group aspect-square overflow-hidden rounded-xl border border-white/10 bg-ash text-left transition-colors hover:border-flame/60"
                    >
                      <img src={src} alt={`${album.title} — ${index + 1}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </button>
                  ))}
                </div>
              ) : <EmptyState title={w.emptyTitle} body={w.emptyBody} action={w.all} target="/photos" />}
            </>
          ) : <EmptyState title={w.notFound} body={w.notFoundBody} action={w.all} target="/photos" />
        ) : (
          <>
            <h1 className="mt-3 font-syne text-5xl font-extrabold tracking-tighter sm:text-6xl lg:text-7xl">{w.title}</h1>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-white/55 sm:text-base">{w.intro}</p>
            {albums.length ? (
              <>
                <h2 className="mt-14 font-syne text-2xl font-bold">{w.albums}</h2>
                <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {albums.map((entry) => (
                    <Link
                      key={entry.slug}
                      to={`/photos/${encodeURIComponent(entry.slug)}`}
                      className="group overflow-hidden rounded-2xl border border-white/10 bg-ash transition-colors hover:border-flame/60"
                    >
                      <div className="relative aspect-[4/3] overflow-hidden bg-coal">
                        {entry.cover ? (
                          <img src={entry.cover} alt={entry.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        ) : <div className="grid h-full place-items-center text-white/30"><ImageIcon className="h-12 w-12" /></div>}
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ash/90 via-transparent to-transparent" />
                      </div>
                      <div className="p-5">
                        <p className="text-xs uppercase tracking-widest text-flame">{dateLabel(entry.date, lang)}</p>
                        <h3 className="mt-2 font-syne text-xl font-bold">{entry.title}</h3>
                        <p className="mt-2 text-xs text-white/45">{(entry.photos || []).length} {w.photos}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            ) : <EmptyState title={w.emptyTitle} body={w.emptyBody} action={w.explore} target="/#parties" />}
          </>
        )}
      </main>
      <Footer />

      {activeImage !== null && images[activeImage] && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${album.title} — ${activeImage + 1}`}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/95 p-4"
          onClick={() => setActiveImage(null)}
        >
          <button type="button" onClick={() => setActiveImage(null)} aria-label={w.close} className="absolute right-4 top-4 z-10 grid h-10 w-10 place-items-center rounded-full bg-white/15 text-white hover:bg-white/25">
            <X className="h-5 w-5" />
          </button>
          <button type="button" onClick={(e) => { e.stopPropagation(); setActiveImage((activeImage + images.length - 1) % images.length); }} aria-label={w.prev} className="absolute left-2 top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/15 text-white hover:bg-white/25 md:left-6">
            <ChevronLeft className="h-6 w-6" />
          </button>
          <img src={images[activeImage]} alt={`${album.title} — ${activeImage + 1}`} className="max-h-[83vh] max-w-[85vw] object-contain" onClick={(e) => e.stopPropagation()} />
          <button type="button" onClick={(e) => { e.stopPropagation(); setActiveImage((activeImage + 1) % images.length); }} aria-label={w.next} className="absolute right-2 top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/15 text-white hover:bg-white/25 md:right-6">
            <ChevronRight className="h-6 w-6" />
          </button>
          <p className="mt-3 text-xs text-white/55">{activeImage + 1} / {images.length}</p>
        </div>
      )}
    </div>
  );
}

function EmptyState({ title, body, action, target }) {
  return (
    <div className="mt-12 flex flex-col items-center rounded-2xl border border-white/10 bg-ash px-6 py-16 text-center">
      <Camera className="h-12 w-12 text-flame" />
      <h2 className="mt-5 font-syne text-2xl font-bold">{title}</h2>
      <p className="mt-3 max-w-lg text-sm leading-relaxed text-white/50">{body}</p>
      <a href={target} className="mt-7 rounded-full border border-white/20 px-5 py-2.5 text-sm font-bold text-white hover:border-flame hover:text-flame">{action}</a>
    </div>
  );
}

export default function Photos() {
  return (
    <LanguageProvider>
      <PhotosBody />
    </LanguageProvider>
  );
}
